import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { getCampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_methods';
import { ICampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_models';
import { getCampaign } from '~/src/dash/api/console-api';
import {
  generalGetLiveLogsV2,
  getAdserverFilledResponseQuery,
  getAdserverInventoryRequestQuery,
} from '~/src/dash/api/live_logs/controller';
import {
  IAdserverFilledResponseData,
  IAdserverInventoryRequestData,
} from '~/src/dash/api/live_logs/models';
import { getAdserverOrder, IAdserverOrder } from '~/src/dash/api/order';
import { getDebugCampaignPacingData } from '~/src/dash/api/pacing/controller';
import { IDebugCampaignPacingModel } from '~/src/dash/api/pacing/models';
import { EBool } from '~/src/dash/api/types/bool';
import { spacing } from '~/src/dash/components/layout/common';
import { LiveLogs } from '~/src/dash/components/LiveLogs';
import { ILiveLogsParsedResponses, ILiveLogsResponse } from '~/src/dash/components/LiveLogs/common';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteCampaignSettingsList,
} from '~/src/dash/router/routes';
import { LoadingStateHasData, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  adserverCreativesToSubgroups,
  CampaignFormView,
  flagBanners,
} from '~/src/dash/views/campaign/campaign-form-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const LineItemContainer = () => {
  const { session, user, location } = useBaseStore();
  const [order, setOrder] = useState<IAdserverOrder | null>(null);
  const [campaign, setCampaign] = useState<IAdserverCampaign | null>(null);
  const [campaignPrebidInfo, setCampaignPrebidInfo] = useState<Array<ICampaignPrebidInfo>>([]);
  const [debugPacingInfo, setDebugPacingInfo] = useState<IDebugCampaignPacingModel | null>(null);
  const [error, setError] = useState<any>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState(LoadingStateLoading);

  // error handling
  const onError = (err: any, message: string = '') => {
    unstable_batchedUpdates(() => {
      setError(err);
      setSnackbarMessage(message);
    });
  };

  // clear error handling
  const onClearError = () => {
    unstable_batchedUpdates(() => {
      setError(null);
      setSnackbarMessage('');
    });
  };

  // get the order that the campaign belongs to
  const fetchOrder = (orderID: string, publisherID: string) => {
    getAdserverOrder(orderID, publisherID, true).then((order: IAdserverOrder) => {
      setOrder(order);
    });
  };

  // get debug info
  const fetchDebugCampaginPacingData = (publisherID: string, campaignID: string) => {
    getDebugCampaignPacingData(publisherID, campaignID).then(([data, err]) => {
      if (!isEmpty(data)) {
        setDebugPacingInfo(data);
      }
      if (err) onError(err);
    });
  };

  // get prebid info
  const fetchPrebidInfo = (campaignID: string, publisherID: string) => {
    getCampaignPrebidInfo(campaignID, publisherID).then(([data, err]) => {
      if (data && !isEmpty(data)) {
        setCampaignPrebidInfo(data);
      }
      if (err) onError(err);
    });
  };

  // get all data for this view
  const fetchData = () => {
    const campaignID = parseLocation(location.pathname).campaign;
    if (!campaignID) return;

    const publisherID = session.publisher.ID;
    setLoadState(LoadingStateLoading);
    getCampaign({
      query: {
        publisher_id: publisherID,
        campaign_id: campaignID,
      },
    })
      .then((campaign: IAdserverCampaign) => {
        campaign.AdserverCreativeFlightDatesSubgroups =
          campaign.EnableFlightDateSubgroups === EBool.EBool_FALSE
            ? []
            : adserverCreativesToSubgroups(campaign.AdserverCreatives);
        campaign.AdserverCreatives =
          campaign.EnableFlightDateSubgroups === EBool.EBool_TRUE
            ? []
            : flagBanners(campaign.AdserverCreatives);
        unstable_batchedUpdates(() => {
          setCampaign(campaign);
          setLoadState(LoadingStateHasData);
        });
        fetchOrder(String(campaign.AdserverOrderID), publisherID);
        fetchDebugCampaginPacingData(publisherID, campaignID);
        fetchPrebidInfo(campaignID, publisherID);
      })
      .catch((err) => {
        onError(
          err,
          "You don't have access to this line item. Please create one or select one in your existing list.",
        );
        setTimeout(() => {
          navigate(
            RouteCampaignSettingsList,
            extractRouteParams({
              session,
              user,
            }),
          );
        }, 3000);
      });
  };

  // API calls for fetching liveLogs , return a Promise that can be await inside the LiveLogs component
  const fetchLiveLogs = async () => {
    if (!campaign) return Promise.reject();
    return Promise.all([
      generalGetLiveLogsV2<Array<IAdserverInventoryRequestData>>(
        getAdserverInventoryRequestQuery(campaign.ID),
        session.publisher.ID,
        user.user.Username,
      ),
      generalGetLiveLogsV2<Array<IAdserverFilledResponseData>>(
        getAdserverFilledResponseQuery(campaign.ID),
        session.publisher.ID,
        user.user.Username,
      ),
    ]);
  };

  // format the data that we get back from fetchLiveLogs
  // to be passed into component LiveLogs so that it can format data whenever its refreshes
  const formatLiveLogs = (responses: Array<ILiveLogsResponse>): ILiveLogsParsedResponses => {
    if (isEmpty(responses)) return [];
    const [adserverRequest, adserverResponse] = responses;
    return [
      {
        key: 'request',
        label: 'Line Item Requests',
        data: adserverRequest,
        config: [
          { key: 'timestamp', label: 'Timestamp', valueType: 'timestamp', isColumn: true },
          { key: 'headerBidderID', label: 'Bidder ID', isColumn: true },
          { key: 'siteID', label: 'Channel ID', isColumn: true },
          { key: 'endpointUri', label: 'Endpoint URI', valueType: 'uri', isColumn: true },
          {
            key: 'endpointRequest',
            label: 'Endpoint Body',
            isColumn: false,
            valueType: 'json',
            isCollapsible: true,
          },
        ],
      },
      {
        key: 'response',
        label: 'Line Item Responses',
        data: adserverResponse,
        config: [
          { key: 'timestamp', label: 'Timestamp', isColumn: true, valueType: 'timestamp' },
          { key: 'headerBidderID', label: 'Bidder ID', isColumn: true },
          { key: 'siteID', label: 'Channel ID', isColumn: true },
          { key: 'creativeID', label: 'Creative ID', isColumn: true },
          { key: 'endpointUri', label: 'Endpoint URI', valueType: 'uri', isColumn: true },
          {
            key: 'endpointRequest',
            label: 'Endpoint Body',
            isColumn: false,
            valueType: 'json',
            isCollapsible: true,
          },
          {
            key: 'response',
            label: 'Response',
            valueType: 'xml',
            isColumn: false,
            isCollapsible: true,
          },
        ],
      },
    ];
  };

  // fetch all data on load
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loadState === LoadingStateLoading ? (
        <LoadingView active={true} text='Loading the line item settings' />
      ) : (
        <div style={{ position: 'relative' }}>
          <CampaignFormView
            campaign={campaign}
            order={order}
            debugPacingInfo={debugPacingInfo}
            campaignPrebidInfo={campaignPrebidInfo}
          />
          <div style={{ position: 'absolute', bottom: spacing.large, right: spacing.base }}>
            <LiveLogs getLiveLogs={fetchLiveLogs} formatLiveLogs={formatLiveLogs} />
          </div>
        </div>
      )}
      <SnackbarView open={!!snackbarMessage} error={error} onClose={onClearError}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
